import * as locales from '@mui/material/locale';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { Locales } from '@/models/User';
import { appText, Translations } from '@/utils/translations/Translations';

import { UsersContext } from './UsersContext';

interface Props {
  locale: Locales | null;
  translatedText: Translations;
  localeLanguageCode: string | null;
  updateLocale: (newLocale: Locales) => void;
  setOverrideLocale: (newLocale: Locales) => void;
  clearOverrideLocale: () => void;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const LocaleContext = createContext<Props>({
  locale: null,
  translatedText: appText['enUS'],
  localeLanguageCode: null,
  updateLocale: () => {},
  setOverrideLocale: () => {},
  clearOverrideLocale: () => {},
});

function getLanguageCodeFromLocale(locale: Locales | null) {
  if (locale === null) return null;
  return locale.substring(0, 2).toLowerCase();
}

function applyLocale(newLocale: Locales) {
  const selectedLocale = Object.keys(locales).find((key) => key === newLocale);
  if (selectedLocale != null) {
    return {
      locale: selectedLocale as Locales,
      languageCode: getLanguageCodeFromLocale(selectedLocale as Locales),
      translatedText: appText[selectedLocale] ?? appText['enUS'],
    };
  }
  return null;
}

export const LocaleProvider = ({ children }: ProviderProps) => {
  const [locale, setLocale] = useState<Locales | null>(null);
  const [localeLanguageCode, setLocaleLanguageCode] = useState<string | null>(null);
  const [translatedText, setTranslatedText] = useState<Translations>(appText['enUS']);
  const [isLocaleOverridden, setIsLocaleOverridden] = useState(false);
  const { userCurrent } = useContext(UsersContext);

  useEffect(() => {
    // Only apply user preference if locale is not overridden
    if (
      !isLocaleOverridden &&
      userCurrent != null &&
      (userCurrent.preferredLanguage ?? 'enUS') != locale
    ) {
      const selectedLocale = userCurrent.preferredLanguage ?? 'enUS';
      setLocale(selectedLocale);

      const languageCode = getLanguageCodeFromLocale(selectedLocale);
      setLocaleLanguageCode(languageCode);

      const localizeTranslatedText = appText[selectedLocale] ?? appText['enUS'];
      setTranslatedText(localizeTranslatedText);
    }
  }, [locale, userCurrent, isLocaleOverridden]);

  const updateLocale = useCallback((updatedLocale: Locales | null): void => {
    if (updatedLocale != null) {
      const result = applyLocale(updatedLocale);
      if (result != null) {
        setLocale(result.locale);
        setLocaleLanguageCode(result.languageCode);
        setTranslatedText(result.translatedText);
      }
    }
  }, []);

  const setOverrideLocale = useCallback((overrideLocale: Locales): void => {
    const result = applyLocale(overrideLocale);
    if (result != null) {
      setLocale(result.locale);
      setLocaleLanguageCode(result.languageCode);
      setIsLocaleOverridden(true);
      setTranslatedText(result.translatedText);
    }
  }, []);

  const clearOverrideLocale = useCallback(() => {
    setIsLocaleOverridden(false);
  }, []);

  return (
    <LocaleContext.Provider
      value={{
        locale,
        translatedText,
        localeLanguageCode,
        updateLocale,
        setOverrideLocale,
        clearOverrideLocale,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
};
